import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from '@/lib/cx';
import { Tooltip } from './Tooltip';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-solid';
export type ButtonSize = 'sm' | 'md';

/*
 * Control heights carry two jobs at once: enterprise density on a desktop
 * (36px / 32px) and a 44px touch target on a phone. The `max-sm:` bump is here
 * rather than at each call site so no surface can forget it.
 */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-2.5 text-xs max-sm:h-10 max-sm:px-3',
  md: 'h-9 gap-2 px-3 text-sm max-sm:h-11 max-sm:px-3.5',
};

const ICON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 w-8 p-0 max-sm:h-10 max-sm:w-10',
  md: 'h-9 w-9 p-0 max-sm:h-11 max-sm:w-11',
};

/*
 * One primary per screen region; everything else is `secondary` (the action,
 * so dark text, not metadata) or `ghost`.
 *
 * `danger` is the ON-PAGE destructive control: a quiet outline at rest that only
 * turns red when the pointer is on it. The red FILL is `danger-solid`, and it
 * belongs inside a confirm dialog, where the decision is actually made — a pink
 * box sitting on every record header at rest is the generated look.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'border-transparent bg-accent-brand text-fg-on-accent hover:bg-accent-brand-hover active:bg-accent-brand-strong',
  secondary:
    'border-border bg-surface text-fg hover:border-hairline-strong active:bg-surface-active',
  ghost:
    'border-transparent bg-transparent text-fg-muted hover:bg-surface-hover hover:text-fg active:bg-surface-active',
  danger:
    'border-border bg-surface text-fg hover:border-danger-border hover:bg-danger-soft hover:text-danger active:bg-danger-soft active:text-danger',
  'danger-solid':
    'border-transparent bg-danger text-fg-on-accent hover:bg-danger/90 active:bg-danger/80',
};

/**
 * The half of the API that means the same thing whichever tag comes out. Only
 * appearance lives here; everything below is about WHICH tag, and what that tag
 * is allowed to be told.
 */
interface ButtonCommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Square control with no label — `aria-label` becomes required in practice. */
  iconOnly?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /**
   * Text for the styled hover label (`Tooltip`). On an icon-only control the
   * label is `tooltip ?? title ?? aria-label`; on a disabled or loading button
   * it is `tooltip ?? title`. Where the styled label renders, the native
   * `title` is NOT also rendered — two labels for one hover.
   */
  tooltip?: string;
}

/**
 * The `<button>` form. Unchanged: every existing call site in the app matches
 * this constituent exactly as it did before the union existed.
 *
 * The four `never`s are the discriminant. They are not decoration — without
 * them `<Button target="_blank">` on a button would be a plain excess-property
 * error whose message names no reason, and a caller could not tell whether the
 * prop was unsupported or merely misspelt.
 */
export interface ButtonProps extends ButtonCommonProps, ButtonHTMLAttributes<HTMLButtonElement> {
  /*
   * Declared explicitly because `ButtonHTMLAttributes` doesn't include it.
   * React 19 passes `ref` through as an ordinary prop, so spreading `...rest`
   * is enough — without this, callers that need the node (focus management,
   * popovers) have to wrap the button and `querySelector` their way back to it.
   */
  ref?: Ref<HTMLButtonElement>;
  /** Renders a spinner and disables the control. */
  loading?: boolean;
  href?: never;
  target?: never;
  rel?: never;
  download?: never;
}

/**
 * The `<a>` form: `href` present, and a real anchor comes out.
 *
 * ── WHY `disabled` AND `loading` ARE REFUSED AT THE TYPE LEVEL ─────────────
 *
 * ⚠️ HTML HAS NO DISABLED LINK, and the three things people reach for instead
 * are each worse than not offering it:
 *
 *   - `pointer-events: none` stops the mouse and nothing else. The anchor is
 *     still in the tab order, still announced as a link, and still followed on
 *     Enter — so it is "disabled" for exactly the one person who was not going
 *     to be harmed by it, and live for everybody using a keyboard.
 *   - `aria-disabled` announces the state and PREVENTS NOTHING. Navigation
 *     still happens; the reader has been told the opposite of what the control
 *     does.
 *   - Swapping to a real `<button>` when disabled works, and is rejected on
 *     purpose: the tag would then change under the reader, so middle-click,
 *     "open in new window" and "copy link address" would be present on a
 *     control sometimes and absent from the same control moments later. An
 *     affordance that comes and goes reads as a broken app, and this is a
 *     primitive — the flicker would be inherited by every future call site.
 *
 * `loading` goes with it, because in this component `loading` MEANS disabled
 * (`disabled={disabled || loading}` below). On an anchor it could not disable
 * anything, so it would render a spinner over a fully live link: a control that
 * says "wait" and navigates if you don't. That is the reassuring direction of
 * wrong, which is the one that never gets reported.
 *
 * The house answer for "there is nowhere to go yet" is already ABSENCE — the
 * same rule that keeps the Xero `sync` button off the screen rather than inert,
 * and that `OpenTrackerSheetButton` follows by rendering `null`. Render
 * nothing, or render the `<button>` form and do the work in `onClick`.
 */
export interface ButtonLinkProps
  extends ButtonCommonProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'type'> {
  ref?: Ref<HTMLAnchorElement>;
  href: string;
  type?: never;
  disabled?: never;
  loading?: never;
}

export type ButtonOrLinkProps = ButtonProps | ButtonLinkProps;

/**
 * `href?: never` types as `undefined`, so this is a real discriminant and not a
 * runtime sniff. Written as a predicate rather than inlined because narrowing a
 * union by an optional key is the kind of thing that silently stops narrowing
 * after an unrelated edit — here it would fail to compile instead.
 */
function isLinkProps(props: ButtonOrLinkProps): props is ButtonLinkProps {
  return props.href !== undefined;
}

/**
 * `rel` for a `target="_blank"` anchor, applied when the caller supplies none.
 *
 * A default rather than a requirement because forgetting it is the failure:
 * the call site that omits it renders, looks right, and is wrong. Modern
 * browsers imply `noopener` for `_blank`, but NOT `noreferrer`, and embedded
 * webviews imply neither — so this is doing real work, not restating the UA.
 *
 * An explicit `rel` from the caller WINS and is not merged: `noopener
 * noreferrer external` and `nofollow` are things a caller may legitimately
 * need, and silently appending to somebody's value would make the rendered
 * attribute something nobody wrote.
 */
const BLANK_TARGET_REL = 'noopener noreferrer';

/**
 * One class string, both tags.
 *
 * Deliberately shared rather than branched: the size, variant and touch-bump
 * rules are house rules about a CONTROL, not about an element, and two copies
 * would agree the day they were written. `disabled:*` is carried onto the
 * anchor too — it can never match there, and dropping it would mean the two
 * forms no longer render the same string, which is the thing worth being able
 * to diff.
 */
function shapeOf(
  variant: ButtonVariant,
  size: ButtonSize,
  iconOnly: boolean,
  className: string | undefined,
) {
  return cx(
    'inline-flex shrink-0 items-center justify-center rounded-md border font-medium',
    'transition-colors duration-150 ease-out motion-reduce:transition-none',
    'disabled:pointer-events-none disabled:opacity-50',
    iconOnly ? ICON_SIZES[size] : SIZES[size],
    VARIANTS[variant],
    className,
  );
}

/**
 * Renders a `<button>`, or an `<a>` when given an `href`.
 *
 * ⚠️ Keyboard behaviour is DIFFERENT between the two forms and that is correct:
 * a button activates on Enter and Space, an anchor on Enter alone. Do not add a
 * key handler to "fix" the anchor — Space is scroll, screen readers announce
 * each element with the keys it really takes, and faking it desynchronises the
 * announcement from the behaviour. Focus rings need no work either: the
 * `:focus-visible` rule in `index.css` matches `a` and `button` in the same
 * selector, so both forms already get the token-coloured outline.
 */
export function Button(props: ButtonOrLinkProps) {
  if (isLinkProps(props)) {
    const {
      variant = 'secondary',
      size = 'md',
      iconOnly = false,
      leadingIcon,
      trailingIcon,
      tooltip,
      className,
      children,
      target,
      rel,
      title,
      'aria-label': ariaLabel,
      ...rest
    } = props;

    // An anchor is never disabled, so only an icon-only link (or an explicit
    // `tooltip`) earns the styled label.
    const label = pick(iconOnly ? (tooltip ?? title ?? ariaLabel) : tooltip);
    const split = label ? splitLayout(className) : { wrapper: '', element: className };

    const anchor = (
      <a
        target={target}
        rel={rel ?? (target === '_blank' ? BLANK_TARGET_REL : undefined)}
        className={shapeOf(variant, size, iconOnly, split.element)}
        title={label ? undefined : title}
        aria-label={ariaLabel ?? (iconOnly ? (title ?? tooltip) : undefined)}
        {...rest}
      >
        {leadingIcon}
        {!iconOnly && children}
        {trailingIcon}
      </a>
    );

    return label ? (
      <Tooltip label={label} className={split.wrapper}>
        {anchor}
      </Tooltip>
    ) : (
      anchor
    );
  }

  const {
    variant = 'secondary',
    size = 'md',
    loading = false,
    iconOnly = false,
    leadingIcon,
    trailingIcon,
    tooltip,
    disabled,
    className,
    children,
    title,
    'aria-label': ariaLabel,
    ...rest
  } = props;

  const inactive = Boolean(disabled || loading);
  /*
   * Which text, if any, becomes the styled label:
   *   - icon-only: `tooltip ?? title ?? aria-label`, always;
   *   - disabled or loading: `tooltip ?? title` — the why-disabled sentence,
   *     which as a native `title` never rendered (`disabled:pointer-events-none`
   *     means the button never receives the hover; the Tooltip's wrapper does);
   *   - an explicit `tooltip` on any other button;
   *   - otherwise none, and a native `title` stays exactly as it was.
   */
  const label = pick(
    iconOnly ? (tooltip ?? title ?? ariaLabel) : inactive ? (tooltip ?? title) : tooltip,
  );
  const split = label ? splitLayout(className) : { wrapper: '', element: className };

  const button = (
    <button
      type="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={shapeOf(variant, size, iconOnly, split.element)}
      title={label ? undefined : title}
      // The native `title` is withheld under a styled label, so an icon-only
      // button with no `aria-label` must not lose its accessible name with it.
      aria-label={ariaLabel ?? (iconOnly ? (title ?? tooltip) : undefined)}
      {...rest}
    >
      {loading ? <Spinner /> : leadingIcon}
      {!iconOnly && children}
      {!loading && trailingIcon}
    </button>
  );

  return label ? (
    <Tooltip label={label} className={split.wrapper}>
      {button}
    </Tooltip>
  ) : (
    button
  );
}

function pick(text: string | undefined): string | undefined {
  return text && text.trim() ? text : undefined;
}

/*
 * ── THE WRAPPER MUST NOT BREAK LAYOUT ───────────────────────────────────────
 *
 * Wrapping a button in a span changes which box is the flex/grid ITEM, so the
 * utilities that describe how the control sits in its PARENT have to reach the
 * wrapper. Split by what each utility does, per token, variant prefixes kept:
 *
 *   MOVE to the wrapper (would double up or misplace if left on both):
 *     margins, self-/justify-self-/place-self-, order-, col-/row- placement,
 *     align- (vertical-align of an inline box), absolute/fixed/sticky and
 *     their inset/top/right/bottom/left/start/end, z-.
 *   BOTH (the wrapper takes the size; the button fills it):
 *     w-/min-w-/max-w-/size-, h-full, flex-1/-auto/-none/-initial/-[…],
 *     grow/shrink/basis, relative, and display utilities (hidden, md:inline-flex
 *     …) so a hidden button leaves no empty span behind to take a gap slot.
 *   Everything else stays on the button alone (justify-*, padding, colour …).
 *
 * The wrapper gets `shrink-0` — the button's own default — unless the caller
 * said something about flexing, so `className="flex-1 sm:flex-none"` still
 * grows and an unadorned icon button still refuses to shrink.
 */
const MOVE =
  /^(m[trblxyse]?|ms|me|self|justify-self|place-self|order|col|row|align|inset|inset-x|inset-y|top|right|bottom|left|start|end|z)-|^(absolute|fixed|sticky)$/;
const BOTH =
  /^(w|min-w|max-w|size|basis|grow|shrink)(-|$)|^flex-(\d|none|auto|initial|\[)|^h-full$|^(relative|static|hidden|block|inline|inline-block|inline-flex|flex|grid|contents|sr-only|not-sr-only)$/;
const FLEXING = /^(flex-(\d|none|auto|initial|\[)|grow|shrink|basis)/;

function baseOf(token: string): string {
  let depth = 0;
  let cut = -1;
  for (let i = 0; i < token.length; i += 1) {
    const ch = token[i];
    if (ch === '[' || ch === '(') depth += 1;
    else if (ch === ']' || ch === ')') depth -= 1;
    else if (ch === ':' && depth === 0) cut = i;
  }
  return token.slice(cut + 1).replace(/^!/, '').replace(/^-/, '');
}

function splitLayout(className: string | undefined): { wrapper: string; element: string } {
  const wrapper: string[] = [];
  const element: string[] = [];
  let flexing = false;
  for (const token of (className ?? '').split(/\s+/).filter(Boolean)) {
    const base = baseOf(token);
    if (FLEXING.test(base)) flexing = true;
    if (MOVE.test(base)) {
      wrapper.push(token);
    } else if (BOTH.test(base)) {
      wrapper.push(token);
      element.push(token);
    } else {
      element.push(token);
    }
  }
  if (!flexing) wrapper.unshift('shrink-0');
  return { wrapper: wrapper.join(' '), element: element.join(' ') };
}

function Spinner() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
